// Full-screen review overlay for a single OutreachDraft. Opens from
// the inline OutreachDraftCard or from the sidebar drafts list.
//
// UI surface (per the step 7 brief):
//   header:  Buyer name + match score + status chip
//   why:     `why_this_buyer` (from the deterministic matcher — not model prose)
//   evidence: structured list with source URLs where available
//   subject: editable (only when status=draft)
//   body:    editable (only when status=draft)
//   contact: dropdown of the acquirer's non-opted-out contacts
//   notes:   review notes (visible in all statuses, editable when draft)
//   actions: [Spara ändringar] [Godkänn] [Avvisa]
//
// Explicitly ABSENT: any Send / Deliver / Skicka Mejl button. The
// caption at the bottom repeats that approval is state-only.

import { useCallback, useEffect, useState } from "react";

import {
  analyzeInboundMessage,
  approveOutreachDraft,
  getBuyerIntelligence,
  getOutreachDraft,
  getOutreachThread,
  prepareReplyToInbound,
  rejectOutreachDraft,
  sendOutreachDraft,
  updateOutreachDraft,
} from "./api";

// Content is editable ONLY when the draft is still a draft or approved.
// Approved edits invalidate approval (backend enforces). Anything
// past that — sending / sent / rejected / failed — is read-only.
const EDITABLE_STATUSES = new Set(["draft", "approved"]);
// The Send button appears only when the backend has told us the draft
// is ready to send (approved with matching content-hash) or when a
// prior delivery failed and the human wants to retry.
const SENDABLE_STATUSES = new Set(["approved", "failed"]);

export default function OutreachReviewOverlay({ draftId, onClose, onChange }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Local edits — synced from server on load; not written back until
  // "Spara ändringar" is clicked. Prevents accidental partial saves.
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [contactId, setContactId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState(null); // "approve"|"reject"|null
  // Send-flow state. `sendBusy` is set only while the POST is in
  // flight. `confirmingSend` toggles the "you're about to email a real
  // person" confirmation banner — critical so a stray click can't
  // dispatch an email.
  const [sendBusy, setSendBusy] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [sendError, setSendError] = useState(null);
  // Step 9/10 — inbound thread state. Loaded on-demand once the
  // overlay opens. Analyses now live PERSISTED on the backend (step
  // 10 InboundMessageAnalysis) and arrive as part of each inbound
  // message payload (`analyses` list + `latest_analysis` shortcut).
  // The overlay no longer holds analyses in local state — clicking
  // "Analysera igen" POSTs to the analyze endpoint (which appends a
  // new version) and then refetches the thread.
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [preparingReplyId, setPreparingReplyId] = useState(null);
  const [analyzeError, setAnalyzeError] = useState(null);
  // Per-message "show earlier versions" toggle. Small local state
  // because it's presentation, not truth.
  const [showAllVersions, setShowAllVersions] = useState({});    // {inbound_id: bool}
  // Step 11 — aggregated buyer intelligence for the draft's acquirer.
  // Fetched lazily once we have draft + workspace + acquirer.
  const [buyerIntel, setBuyerIntel] = useState(null);
  const [buyerIntelLoading, setBuyerIntelLoading] = useState(false);

  // ── Load on open ────────────────────────────────────────────────
  useEffect(() => {
    if (!draftId) return;
    const controller = new AbortController();
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const d = await getOutreachDraft(draftId, { signal: controller.signal });
        if (!alive) return;
        _applyDraft(d);
      } catch (e) {
        if (!alive || e.name === "AbortError") return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda utkastet.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // ── Load thread (step 9) — only after outbound is sent ─────────
  // Fresh cold-outreach drafts have no thread yet; skip the round-trip.
  useEffect(() => {
    if (!draftId || !draft) return;
    if (draft.status !== "sent" && draft.status !== "failed") return;
    let alive = true;
    setThreadLoading(true);
    (async () => {
      try {
        const t = await getOutreachThread(draftId);
        if (!alive) return;
        setThread(t);
      } catch {
        // Non-fatal — thread just doesn't render if the fetch fails.
      } finally {
        if (alive) setThreadLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, draft?.status]);

  // Step 11 — load aggregated buyer intelligence once we know the
  // draft's acquirer + workspace. Re-fetched whenever an analyze POST
  // completes (new items may have been derived).
  useEffect(() => {
    if (!draft?.acquirer?.id || !draft?.workspace_id) return;
    let alive = true;
    setBuyerIntelLoading(true);
    (async () => {
      try {
        const bi = await getBuyerIntelligence(draft.acquirer.id, {
          workspaceId: draft.workspace_id,
        });
        if (!alive) return;
        setBuyerIntel(bi);
      } catch {
        // Non-fatal — the section just doesn't render if the fetch
        // fails (e.g. workspace access lost mid-session).
      } finally {
        if (alive) setBuyerIntelLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.acquirer?.id, draft?.workspace_id, thread?.inbound_count]);

  const handleAnalyze = useCallback(async (inboundId) => {
    setAnalyzingId(inboundId);
    setAnalyzeError(null);
    try {
      const result = await analyzeInboundMessage(inboundId);
      if (result?.ok === false) {
        setAnalyzeError({
          inboundId,
          detail: result.detail || "Analys misslyckades.",
        });
      } else {
        // Refetch the thread so the persisted new version appears
        // consistently — no local-vs-server drift.
        try {
          const t = await getOutreachThread(draftId);
          setThread(t);
        } catch {
          /* non-fatal — user can reopen the overlay to sync */
        }
        // Step 11 — new analysis derived new intelligence items;
        // refetch the aggregation so the Köparinsikt section stays
        // in sync.
        if (draft?.acquirer?.id && draft?.workspace_id) {
          try {
            const bi = await getBuyerIntelligence(draft.acquirer.id, {
              workspaceId: draft.workspace_id,
            });
            setBuyerIntel(bi);
          } catch {
            /* non-fatal */
          }
        }
      }
    } catch (e) {
      setAnalyzeError({
        inboundId,
        detail: e?.body?.detail || e.message || "Analys misslyckades.",
      });
    } finally {
      setAnalyzingId(null);
    }
  }, [draftId, draft?.acquirer?.id, draft?.workspace_id]);

  const handlePrepareReply = useCallback(async (inboundId) => {
    setPreparingReplyId(inboundId);
    try {
      const res = await prepareReplyToInbound(inboundId);
      const newDraftId = res?.reply_draft_id;
      onChange?.({ prepared_reply_draft_id: newDraftId });
      // Close this overlay and let the caller open the new draft.
      onClose?.();
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte förbereda svar.");
    } finally {
      setPreparingReplyId(null);
    }
  }, [onChange, onClose]);

  const _applyDraft = useCallback((d) => {
    setDraft(d);
    setSubject(d.subject || "");
    setBody(d.body || "");
    setReviewNotes(d.review_notes || "");
    setContactId(d.contact?.id ?? null);
  }, []);

  const editable = draft && EDITABLE_STATUSES.has(draft.status);
  const sendable = draft && SENDABLE_STATUSES.has(draft.status);
  const sent = draft && draft.status === "sent";
  const failed = draft && draft.status === "failed";
  const sending = draft && draft.status === "sending";

  const hasLocalEdits = useCallback(() => {
    if (!draft) return false;
    return (
      subject !== (draft.subject || "") ||
      body !== (draft.body || "") ||
      reviewNotes !== (draft.review_notes || "") ||
      contactId !== (draft.contact?.id ?? null)
    );
  }, [draft, subject, body, reviewNotes, contactId]);

  // ── Save edits ──────────────────────────────────────────────────
  const handleSave = async () => {
    if (!draft || !editable) return;
    if (!hasLocalEdits()) return;
    setSaving(true);
    setError(null);
    try {
      const patch = {};
      if (subject !== (draft.subject || "")) patch.subject = subject;
      if (body !== (draft.body || "")) patch.body = body;
      if (reviewNotes !== (draft.review_notes || "")) patch.review_notes = reviewNotes;
      if (contactId !== (draft.contact?.id ?? null)) patch.contact_id = contactId;
      const updated = await updateOutreachDraft(draft.id, patch);
      _applyDraft(updated);
      onChange?.(updated);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte spara ändringarna.");
    } finally {
      setSaving(false);
    }
  };

  // ── Send (human-only) ──────────────────────────────────────────
  // Explicit two-step: first click surfaces the confirmation banner
  // showing the exact recipient; second click actually POSTs. This is
  // the only frontend path that can initiate outbound email.
  const handleSendClick = () => {
    setSendError(null);
    setConfirmingSend(true);
  };

  const handleSendCancel = () => {
    setConfirmingSend(false);
    setSendError(null);
  };

  const handleSendConfirm = async () => {
    if (!draft || !sendable) return;
    setSendBusy(true);
    setSendError(null);
    try {
      const updated = await sendOutreachDraft(draft.id);
      _applyDraft(updated);
      onChange?.(updated);
      setConfirmingSend(false);
    } catch (e) {
      // Backend returns 409/400 with a plain-text detail for state
      // errors (already sent, opted-out contact, hash mismatch etc.).
      // 502 for provider failure — still shows the specific error.
      const detail = e?.body?.detail || e.message || "Leveransen misslyckades.";
      setSendError(detail);
      // On provider failure, backend has already persisted
      // status=failed. Reload so the UI reflects reality.
      try {
        const fresh = await getOutreachDraft(draft.id);
        _applyDraft(fresh);
        onChange?.(fresh);
      } catch {
        /* keep the pre-error state */
      }
    } finally {
      setSendBusy(false);
    }
  };

  // ── Approve / reject ────────────────────────────────────────────
  const handleDecision = async (kind) => {
    if (!draft || !editable) return;
    // If the reviewer typed notes but hasn't saved, prompt to include them.
    let notes = reviewNotes.trim();
    if (!notes && kind === "reject") {
      const typed = window.prompt("Ange en kort anledning för avvisning:");
      if (typed == null) return;
      notes = typed.trim();
    }
    setDecisionBusy(kind);
    setError(null);
    try {
      const call = kind === "approve" ? approveOutreachDraft : rejectOutreachDraft;
      const updated = await call(draft.id, {
        reviewNotes: notes || undefined,
      });
      _applyDraft(updated);
      onChange?.(updated);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Åtgärden misslyckades.");
    } finally {
      setDecisionBusy(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="qai-review-overlay" role="dialog" aria-modal="true">
      <div className="qai-review-shell">
        <header className="qai-review-header">
          <div>
            <div className="qai-review-title">Granska outreach-utkast</div>
            {draft?.acquirer?.name && (
              <div className="qai-review-buyer">{draft.acquirer.name}</div>
            )}
          </div>
          <div className="qai-review-header-right">
            {draft && (
              <>
                <span className="qai-outreach-score">
                  {typeof draft.match_score === "number" ? `${draft.match_score}/100` : "—"}
                </span>
                <span
                  className="qai-outreach-status-chip"
                  data-status={draft.status}
                >
                  {statusLabel(draft.status)}
                </span>
              </>
            )}
            <button
              type="button"
              className="qai-review-close"
              onClick={onClose}
              aria-label="Stäng"
            >
              ×
            </button>
          </div>
        </header>

        {loading && <div className="qai-review-loading">Laddar utkast…</div>}
        {error && <div className="qai-error qai-error-inline">{error}</div>}

        {draft && (
          <div className="qai-review-body">
            {/* WHY THIS BUYER */}
            {draft.why_this_buyer && (
              <section className="qai-review-section">
                <h3>Varför denna köpare</h3>
                <pre className="qai-review-why">{draft.why_this_buyer}</pre>
              </section>
            )}

            {/* EVIDENCE */}
            {Array.isArray(draft.evidence) && draft.evidence.length > 0 && (
              <section className="qai-review-section">
                <h3>Evidence</h3>
                <ul className="qai-review-evidence">
                  {draft.evidence.map((e, i) => (
                    <li key={i}>
                      <span className="qai-review-evidence-kind" data-kind={e.kind || "fact"}>
                        {evidenceKindLabel(e.kind)}
                      </span>
                      <span className="qai-review-evidence-label">{e.label}</span>
                      {e.source_url && (
                        <>
                          {" "}
                          <a
                            className="qai-review-evidence-src"
                            href={e.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            källa
                          </a>
                        </>
                      )}
                      {typeof e.confidence === "number" && (
                        <span className="qai-review-evidence-conf">
                          {" · "}{Math.round(e.confidence * 100)}% konfidens
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* SUBJECT */}
            <section className="qai-review-section">
              <h3>Ämne</h3>
              <input
                type="text"
                className="qai-review-subject"
                value={subject}
                maxLength={255}
                disabled={!editable || saving || !!decisionBusy}
                onChange={(e) => setSubject(e.target.value)}
              />
            </section>

            {/* BODY */}
            <section className="qai-review-section">
              <h3>Brödtext</h3>
              <textarea
                className="qai-review-textarea"
                value={body}
                rows={12}
                disabled={!editable || saving || !!decisionBusy}
                onChange={(e) => setBody(e.target.value)}
              />
              {draft.revision?.is_human_edited && (
                <details className="qai-review-original">
                  <summary>
                    Visa AI:ns ursprungliga version (redigerad{" "}
                    {draft.revision.edit_count} gång{draft.revision.edit_count === 1 ? "" : "er"})
                  </summary>
                  <pre>{draft.revision.original_body}</pre>
                </details>
              )}
            </section>

            {/* CONTACT */}
            {draft.contact !== null && (
              <section className="qai-review-section">
                <h3>Kontakt</h3>
                <div className="qai-review-contact">
                  <div className="qai-review-contact-name">
                    {draft.contact?.name || "(ingen vald)"}
                  </div>
                  <div className="qai-review-contact-meta">
                    {draft.contact?.title && <span>{draft.contact.title}</span>}
                    {draft.contact?.email && <span>{draft.contact.email}</span>}
                    <span className="qai-review-contact-source">
                      källa: {draft.contact?.source}
                    </span>
                  </div>
                </div>
              </section>
            )}

            {/* REVIEW NOTES */}
            <section className="qai-review-section">
              <h3>Anteckningar</h3>
              <textarea
                className="qai-review-textarea qai-review-notes"
                value={reviewNotes}
                rows={3}
                placeholder="Interna anteckningar inför godkännande eller avvisning."
                disabled={!editable || saving || !!decisionBusy}
                onChange={(e) => setReviewNotes(e.target.value)}
              />
            </section>

            {/* GENERATED-BY / REVIEWER META */}
            <section className="qai-review-section qai-review-meta">
              {draft.generated_by && (
                <div>Genererad av: {draft.generated_by.full_name}</div>
              )}
              {draft.approved_by && (
                <div>Godkänd av: {draft.approved_by.full_name} · {shortDate(draft.approved_at)}</div>
              )}
              {draft.rejected_by && (
                <div>Avvisad av: {draft.rejected_by.full_name} · {shortDate(draft.rejected_at)}</div>
              )}
              {draft.last_edited_by && (
                <div>Senast redigerad av: {draft.last_edited_by.full_name} · {shortDate(draft.last_edited_at)}</div>
              )}
              {draft.delivery?.sent_by && (
                <div>Skickad av: {draft.delivery.sent_by.full_name} · {shortDate(draft.delivery.sent_at)}</div>
              )}
            </section>

            {/* REPLIES + BOUNCES (step 9) */}
            {(thread?.inbound_messages?.length > 0 || thread?.bounces?.length > 0 || threadLoading) && (
              <section className="qai-review-section qai-review-thread">
                <h3>Köparsvar</h3>
                {threadLoading && !thread && (
                  <div className="qai-review-loading">Hämtar tråd…</div>
                )}
                {thread?.bounces?.map((b) => (
                  <div key={`b-${b.id}`} className="qai-inbound-bounce">
                    <div className="qai-inbound-badge qai-inbound-badge-bounce">
                      {b.kind === "complaint" ? "COMPLAINT" : "BOUNCE"}
                    </div>
                    <div className="qai-inbound-meta">
                      <div>{b.from_email} · {shortDate(b.received_at)}</div>
                      {b.reason && <div className="qai-inbound-reason">{b.reason}</div>}
                    </div>
                  </div>
                ))}
                {thread?.inbound_messages?.map((m) => {
                  const latest = m.latest_analysis;
                  const olderVersions = (m.analyses || []).slice(1);
                  const showEarlier = !!showAllVersions[m.id];
                  return (
                    <div key={`in-${m.id}`} className="qai-inbound-card">
                      <div className="qai-inbound-head">
                        <div>
                          <div className="qai-inbound-sender">
                            <span className="qai-inbound-direction-tag">RECEIVED</span>{" "}
                            {m.from_name ? `${m.from_name} · ` : ""}{m.from_email}
                          </div>
                          <div className="qai-inbound-when">{shortDate(m.received_at)}</div>
                        </div>
                        <div className="qai-inbound-status-chip" data-status={m.match_status}>
                          {m.match_status === "matched" ? "Kopplad" : "Okopplad"}
                        </div>
                      </div>
                      {m.subject && <div className="qai-inbound-subject">Ämne: {m.subject}</div>}
                      <pre className="qai-inbound-body">{m.body_text || "(tom text)"}</pre>

                      {/* Persisted analysis (step 10). Latest version by
                          default; earlier versions on demand. */}
                      {latest && (
                        <div className="qai-analysis-wrap">
                          <AnalysisVersionHeader
                            version={latest.version}
                            model={latest.model}
                            createdAt={latest.created_at}
                            createdBy={latest.created_by}
                            totalCount={m.analysis_count}
                          />
                          <AnalysisBlock a={latest} />
                          {olderVersions.length > 0 && (
                            <details
                              className="qai-analysis-history"
                              open={showEarlier}
                              onToggle={(e) =>
                                setShowAllVersions((prev) => ({
                                  ...prev,
                                  [m.id]: e.target.open,
                                }))
                              }
                            >
                              <summary>
                                Visa tidigare versioner ({olderVersions.length})
                              </summary>
                              {olderVersions.map((v) => (
                                <div key={`v-${v.id}`} className="qai-analysis-older">
                                  <AnalysisVersionHeader
                                    version={v.version}
                                    model={v.model}
                                    createdAt={v.created_at}
                                    createdBy={v.created_by}
                                  />
                                  <AnalysisBlock a={v} />
                                </div>
                              ))}
                            </details>
                          )}
                        </div>
                      )}
                      {analyzeError?.inboundId === m.id && (
                        <div className="qai-error qai-error-inline">
                          {analyzeError.detail}
                        </div>
                      )}

                      <div className="qai-inbound-actions">
                        <button
                          type="button"
                          className="qai-review-btn qai-review-btn-secondary"
                          onClick={() => handleAnalyze(m.id)}
                          disabled={analyzingId === m.id}
                        >
                          {analyzingId === m.id
                            ? "Analyserar…"
                            : latest
                              ? "Analysera igen"
                              : "Analysera svaret"}
                        </button>
                        <button
                          type="button"
                          className="qai-review-btn qai-review-btn-approve"
                          onClick={() => handlePrepareReply(m.id)}
                          disabled={preparingReplyId === m.id}
                          title="Skapar ett svarsutkast som du sedan granskar + skickar manuellt"
                        >
                          {preparingReplyId === m.id
                            ? "Förbereder…"
                            : "Förbered svar"}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div className="qai-review-footer-note">
                  Systemet svarar INTE automatiskt. Att förbereda ett svar skapar
                  ett utkast som måste godkännas och skickas manuellt.
                </div>
              </section>
            )}

            {/* KÖPARINSIKT (step 11) — aggregated view for this acquirer */}
            {(buyerIntel || buyerIntelLoading) && (
              <BuyerIntelSection
                intel={buyerIntel}
                loading={buyerIntelLoading}
              />
            )}

            {/* DELIVERY STATE (step 8) */}
            {(sent || failed || sending) && (
              <section className="qai-review-section qai-review-delivery">
                {sent && (
                  <div className="qai-delivery-sent">
                    <div className="qai-delivery-badge qai-delivery-badge-sent">Skickat</div>
                    <div className="qai-delivery-meta">
                      <div>Till: {draft.delivery?.sent_to_email || "(okänd mottagare)"}</div>
                      <div>Från: {draft.delivery?.sent_from_email || "—"}</div>
                      <div>Tid: {shortDate(draft.delivery?.sent_at)}</div>
                      {draft.delivery?.provider && (
                        <div>Leverantör: {draft.delivery.provider}</div>
                      )}
                      {draft.delivery?.provider_message_id && (
                        <div className="qai-delivery-msgid">
                          Message-ID: <code>{draft.delivery.provider_message_id}</code>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {failed && (
                  <div className="qai-delivery-failed">
                    <div className="qai-delivery-badge qai-delivery-badge-failed">Leverans misslyckades</div>
                    <div className="qai-delivery-meta">
                      <div>Försök: {draft.delivery?.attempts ?? 0}</div>
                      {draft.delivery?.last_attempt_at && (
                        <div>Senaste försök: {shortDate(draft.delivery.last_attempt_at)}</div>
                      )}
                      {draft.delivery?.error && (
                        <div className="qai-delivery-error-text">Fel: {draft.delivery.error}</div>
                      )}
                    </div>
                  </div>
                )}
                {sending && (
                  <div className="qai-delivery-sending">
                    <div className="qai-delivery-badge qai-delivery-badge-sending">Skickar…</div>
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        {/* ACTIONS */}
        {draft && (
          <footer className="qai-review-footer">
            {/* Confirmation banner appears only after the human clicks
                the Send button, before the actual POST fires. Shows
                the exact recipient so nothing is hidden. */}
            {confirmingSend && sendable && draft.contact?.email && (
              <div className="qai-send-confirm">
                <div className="qai-send-confirm-title">
                  Bekräfta utskick
                </div>
                <div className="qai-send-confirm-text">
                  Detta skickar det godkända mejlet till{" "}
                  <strong>{draft.contact.email}</strong>. Detta är en
                  extern kommunikation — mottagaren kommer att få ett
                  riktigt mejl.
                </div>
                {sendError && (
                  <div className="qai-send-confirm-error">{sendError}</div>
                )}
                <div className="qai-send-confirm-actions">
                  <button
                    type="button"
                    className="qai-review-btn qai-review-btn-secondary"
                    onClick={handleSendCancel}
                    disabled={sendBusy}
                  >
                    Avbryt
                  </button>
                  <button
                    type="button"
                    className="qai-review-btn qai-review-btn-send"
                    onClick={handleSendConfirm}
                    disabled={sendBusy}
                  >
                    {sendBusy ? "Skickar…" : `Ja, skicka till ${draft.contact.email}`}
                  </button>
                </div>
              </div>
            )}

            {sendError && !confirmingSend && (
              <div className="qai-error qai-error-inline">{sendError}</div>
            )}

            <div className="qai-review-footer-note">
              {sent
                ? "Detta utkast har levererats. Ingen automatisk uppföljning eller ytterligare kontakt sker."
                : sendable
                ? "Klicka på Skicka mejl för att leverera det godkända utkastet. En extern kommunikation initieras — du är den enda som kan starta detta."
                : "Godkännande förbereder utkastet för framtida leverans. Ingen AI-åtgärd kan skicka — endast en autentiserad användare via knappen Skicka mejl."}
            </div>
            <div className="qai-review-footer-actions">
              {/* Save / Approve / Reject shown for draft + approved
                  (editing an approved draft demotes it). Send shown
                  for approved + failed (retry). Sent = no actions. */}
              {editable && (
                <>
                  <button
                    type="button"
                    className="qai-review-btn qai-review-btn-secondary"
                    onClick={handleSave}
                    disabled={saving || !!decisionBusy || sendBusy || !hasLocalEdits()}
                  >
                    {saving ? "Sparar…" : "Spara ändringar"}
                  </button>
                  <button
                    type="button"
                    className="qai-review-btn qai-review-btn-reject"
                    onClick={() => handleDecision("reject")}
                    disabled={!!decisionBusy || saving || sendBusy}
                  >
                    {decisionBusy === "reject" ? "Avvisar…" : "Avvisa"}
                  </button>
                  {draft.status === "draft" && (
                    <button
                      type="button"
                      className="qai-review-btn qai-review-btn-approve"
                      onClick={() => handleDecision("approve")}
                      disabled={!!decisionBusy || saving || sendBusy}
                    >
                      {decisionBusy === "approve" ? "Godkänner…" : "Godkänn"}
                    </button>
                  )}
                </>
              )}
              {sendable && !confirmingSend && (
                <button
                  type="button"
                  className="qai-review-btn qai-review-btn-send"
                  onClick={handleSendClick}
                  disabled={
                    sendBusy || saving || !!decisionBusy ||
                    !draft.contact?.email
                  }
                  title={
                    !draft.contact?.email
                      ? "Välj en kontakt med mejladress först"
                      : "Skickar ett riktigt mejl till mottagaren"
                  }
                >
                  {failed ? "Försök skicka igen" : "Skicka mejl"}
                </button>
              )}
            </div>
          </footer>
        )}
      </div>
      <div
        className="qai-review-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
    </div>
  );
}

function BuyerIntelSection({ intel, loading }) {
  if (loading && !intel) {
    return (
      <section className="qai-review-section">
        <h3>Köparinsikt</h3>
        <div className="qai-review-loading">Laddar…</div>
      </section>
    );
  }
  if (!intel) return null;

  const comms = intel.communication || {};
  const hasAnyIntel =
    intel.interest ||
    intel.information_requested?.length ||
    intel.questions_asked?.length ||
    intel.objections?.length ||
    intel.requirements?.length ||
    intel.next_steps_proposed?.length ||
    intel.uncertainty?.length;

  return (
    <section className="qai-review-section qai-buyer-intel">
      <h3>Köparinsikt</h3>

      {/* Communication summary */}
      <div className="qai-bi-summary">
        <span>
          <strong>{comms.inbound_count || 0}</strong> köparsvar
        </span>
        <span>·</span>
        <span>
          <strong>{comms.outbound_count || 0}</strong> utskickade utkast
        </span>
        {comms.last_inbound_at && (
          <>
            <span>·</span>
            <span>Senaste svar: {shortDate(comms.last_inbound_at)}</span>
          </>
        )}
        {comms.bounce_count > 0 && (
          <>
            <span>·</span>
            <span className="qai-bi-bounce-count">
              {comms.bounce_count} leveransfel
            </span>
          </>
        )}
      </div>

      {/* Latest interest signal */}
      {intel.interest && (
        <div className="qai-bi-interest">
          <span className="qai-analysis-signal" data-signal={intel.interest.signal}>
            {String(intel.interest.signal || "").toUpperCase()}
          </span>
          <span className="qai-bi-interest-note">
            Senaste intressesignal (inferens från analys v
            {intel.interest.provenance?.analysis_version})
          </span>
        </div>
      )}

      {!hasAnyIntel && !loading && (
        <div className="qai-bi-empty">
          Inga köparinsikter registrerade än. Analysera köparens svar
          för att extrahera frågor, önskemål och krav.
        </div>
      )}

      <BuyerIntelBucket title="Begärd information" items={intel.information_requested} />
      <BuyerIntelBucket title="Öppna frågor" items={intel.questions_asked} />
      <BuyerIntelBucket title="Invändningar" items={intel.objections} />
      <BuyerIntelBucket title="Uttalade krav" items={intel.requirements} />
      <BuyerIntelBucket title="Föreslagna nästa steg" items={intel.next_steps_proposed} />
      {intel.uncertainty?.length > 0 && (
        <BuyerIntelBucket
          title="Osäkerhet i analysen"
          items={intel.uncertainty}
        />
      )}

      {/* Suggested actions — labeled suggestions, no auto-execution */}
      {intel.suggested_actions?.length > 0 && (
        <div className="qai-bi-suggestions">
          <div className="qai-bi-suggestions-title">
            FÖRSLAG (kräver manuell handling)
          </div>
          <ul>
            {intel.suggested_actions.map((s, i) => (
              <li key={i} className="qai-bi-suggestion">
                <div className="qai-bi-suggestion-label">{s.label}</div>
                {s.detail && <div className="qai-bi-suggestion-detail">{s.detail}</div>}
                <div className="qai-bi-suggestion-tag">
                  Endast förslag — inget utförs automatiskt
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="qai-bi-disclaimer">
        {intel.assistive_only}
      </div>
    </section>
  );
}

function BuyerIntelBucket({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="qai-bi-bucket">
      <div className="qai-bi-bucket-title">
        {title} <span className="qai-bi-bucket-count">({items.length})</span>
      </div>
      <ul>
        {items.map((it) => (
          <li key={it.id ?? `${it.type}-${it.text}`} className="qai-bi-item">
            <span
              className={
                "qai-analysis-tag qai-analysis-tag-" +
                (it.fact_or_inference || "inference")
              }
              title="Klassificering från källanalysen — inte omtolkad"
            >
              {it.fact_or_inference === "fact" ? "FAKT" : "INF"}
            </span>{" "}
            <span className="qai-bi-item-text">{it.text}</span>
            {it.provenance && (
              <span className="qai-bi-item-prov" title="Källa">
                {" "}
                (msg #{it.provenance.source_inbound_message_id}, analys v
                {it.provenance.analysis_version})
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AnalysisVersionHeader({ version, model, createdAt, createdBy, totalCount }) {
  // Small provenance line above each analysis rendering: which version,
  // which model, when, by whom, and (on the latest) how many exist.
  return (
    <div className="qai-analysis-version">
      <span className="qai-analysis-version-chip">v{version}</span>{" "}
      <span className="qai-analysis-version-model">{model || "modell?"}</span>{" "}
      <span className="qai-analysis-version-when">· {shortDate(createdAt)}</span>
      {createdBy && (
        <span className="qai-analysis-version-by"> · {createdBy.username}</span>
      )}
      {typeof totalCount === "number" && totalCount > 1 && (
        <span className="qai-analysis-version-total">
          {" "}
          · {totalCount} versioner totalt
        </span>
      )}
    </div>
  );
}

function AnalysisBlock({ a }) {
  // Renders the structured analysis produced by the bounded LLM. Every
  // line is a real item from the buyer's message OR is explicitly
  // labeled as inference — never both silently mixed.
  if (!a) return null;
  const item = (v) => (
    <>
      <span className={`qai-analysis-tag qai-analysis-tag-${v.type}`}>
        {v.type === "fact" ? "FAKT" : "INF"}
      </span>{" "}
      {v.text}
    </>
  );
  return (
    <div className="qai-analysis">
      <div className="qai-analysis-summary">
        <span className="qai-analysis-signal" data-signal={a.interest_signal}>
          {(a.interest_signal || "unclear").toUpperCase()}
        </span>{" "}
        {a.summary_one_line}
      </div>
      {a.questions_asked?.length > 0 && (
        <AnalysisList title="Frågor" items={a.questions_asked} render={item} />
      )}
      {a.information_requested?.length > 0 && (
        <AnalysisList title="Begärt underlag" items={a.information_requested} render={item} />
      )}
      {a.objections_or_concerns?.length > 0 && (
        <AnalysisList title="Invändningar" items={a.objections_or_concerns} render={item} />
      )}
      {a.stated_requirements?.length > 0 && (
        <AnalysisList title="Uttalade krav" items={a.stated_requirements} render={item} />
      )}
      {a.next_steps_proposed?.length > 0 && (
        <AnalysisList title="Föreslagna nästa steg" items={a.next_steps_proposed} render={item} />
      )}
      {a.uncertain?.length > 0 && (
        <div className="qai-analysis-uncertain">
          <strong>Osäkert:</strong> {a.uncertain.join(" · ")}
        </div>
      )}
    </div>
  );
}

function AnalysisList({ title, items, render }) {
  return (
    <div className="qai-analysis-list">
      <div className="qai-analysis-list-title">{title}</div>
      <ul>
        {items.map((it, i) => (
          <li key={i}>{render(it)}</li>
        ))}
      </ul>
    </div>
  );
}

function statusLabel(s) {
  switch (s) {
    case "draft":     return "Utkast";
    case "approved":  return "Godkänt";
    case "rejected":  return "Avvisat";
    case "sending":   return "Skickar…";
    case "sent":      return "Skickat";
    case "failed":    return "Misslyckades";
    default:          return s || "—";
  }
}

function evidenceKindLabel(kind) {
  switch (kind) {
    case "acquisition":   return "FÖRVÄRV";
    case "profile":       return "PROFIL";
    case "seller_fact":   return "SÄLJARE";
    case "match_component": return "MATCH";
    default:              return "EVIDENCE";
  }
}

function shortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
